/**
 * Load All Ionic Components Utility Component
 * 
 * DEVELOPMENT-ONLY COMPONENT: This component addresses a critical issue in the current
 * Angular/Ionic version where Ionic components that are not rendered immediately upon
 * application startup lose their imports and cause runtime errors when later rendered.
 * 
 * Purpose:
 * - Preloads all Ionic UI components used throughout the application
 * - Ensures components are initialized and cached for immediate use
 * - Prevents "component not found" errors during development
 * - Only rendered in development mode via app.component.html
 * 
 * Technical Issue Addressed:
 * - Angular/Ionic development mode has a bug where lazy-loaded Ionic components
 *   lose their import references if not rendered immediately
 * - This causes runtime errors when components are later rendered dynamically
 * - Preloading forces all components to be registered and cached upfront
 * 
 * Preloading Strategy:
 * - Renders Ionic components offscreen with CSS classes to hide them
 * - Immediately presents and dismisses components to trigger initialization
 * - Components are cached in memory for faster future rendering
 * - Uses minimal duration/delays to minimize performance impact
 * 
 * Components Preloaded:
 * - ToastController: For notification messages
 * - AlertController: For dialog boxes and confirmations
 * - ActionSheetController: For bottom sheet menus
 * - IonModal: For modal dialogs and overlays
 * 
 * Usage:
 * - Only included in app.component.html during development
 * - Automatically runs on component initialization
 * - No user interaction required
 * - Components are hidden offscreen during preloading
 * 
 * Dependencies:
 * - ToastController: To create and display toast messages
 * - AlertController: To preload alert dialogs
 * - ActionSheetController: To preload action sheets
 * - IonModal: Preloads the modal component
 * 
 * @author MacSafety Development Team
 * @version 1.0
 * @note DEVELOPMENT MODE ONLY - Remove from production builds
 */

import { Component, ViewChild, OnInit } from '@angular/core';
import { IonModal } from '@ionic/angular';
import { OverlayEventDetail } from '@ionic/core/components';
import { ToastController } from '@ionic/angular';
import { AlertController } from '@ionic/angular';
import { ActionSheetController } from '@ionic/angular';
import { IonSelectModal } from '@ionic/angular';

@Component({
  selector: 'app-load-all-ionic',
  templateUrl: './load-all-ionic.component.html',
  styleUrls: ['./load-all-ionic.component.css'],
})
export class LoadAllIonicComponent implements OnInit {
  // Private controllers for preloading Ionic components
  private _toastController: ToastController;
  private _alertController: AlertController;
  private _actionSheetController: ActionSheetController;
  
  // ViewChild reference to the modal for testing purposes
  @ViewChild(IonModal) modal!: IonModal;

  // Test properties for modal functionality demonstration
  message = 'This modal example uses triggers to automatically open a modal when the button is clicked.';
  name!: string;

  constructor(
    toastController: ToastController, 
    alertController: AlertController, 
    actionSheetController: ActionSheetController
  ) {
    this._toastController = toastController;
    this._alertController = alertController;
    this._actionSheetController = actionSheetController;
  }

  /**
   * Component initialization - triggers preloading of all Ionic components
   * This method runs automatically when the component is created
   */
  async ngOnInit(): Promise<void> {
    // Load all Ionic components to prevent import loss issues
    this.openToast();
    await this.preloadAlert();
    await this.preloadActionSheet();
  }

  /**
   * Preloads the ToastController by creating and displaying a test toast
   * Toast is made invisible via CSS and has minimal duration
   * This ensures the toast component is cached and ready for immediate use
   */
  async openToast() {
    const toast = await this._toastController.create({
      message: 'This is a test toast (dev mode only)',
      cssClass: 'toast-invisible', // CSS class hides the toast from view
      duration: 20, // Minimal duration to minimize visual impact
    });
    await toast.present();
  }

  /**
   * Preloads the AlertController by creating and immediately dismissing a test alert
   * Alert is positioned offscreen via CSS class to avoid visual interference
   * This ensures alert dialogs are cached and ready for immediate use
   */
  async preloadAlert() {
    const alert = await this._alertController.create({
      header: 'Alert',
      message: 'This is an alert',
      buttons: ['OK'],
      cssClass: 'offscreen-alert' // CSS class positions alert offscreen
    });
    
    // Present the alert to trigger component initialization
    await alert.present();
    
    // Dismiss after minimal delay to ensure component is fully loaded and cached
    setTimeout(async () => {
      await alert.dismiss();
    }, 10);
  }

  /**
   * Preloads the ActionSheetController by creating and immediately dismissing a test action sheet
   * Action sheet is positioned offscreen via CSS class to avoid visual interference
   * This ensures action sheets are cached and ready for immediate use
   */
  async preloadActionSheet() {
    const actionSheet = await this._actionSheetController.create({
      header: 'Preload Actions',
      cssClass: 'offscreen-actionsheet', // CSS class positions action sheet offscreen
      buttons: [
        {
          text: 'Test Action 1',
          icon: 'checkmark-outline',
          handler: () => {
            // Empty handler for preload - no actual functionality needed
          }
        },
        {
          text: 'Test Action 2',
          icon: 'star-outline',
          handler: () => {
            // Empty handler for preload - no actual functionality needed
          }
        },
        {
          text: 'Delete',
          role: 'destructive',
          icon: 'trash-outline',
          handler: () => {
            // Empty handler for preload - no actual functionality needed
          }
        },
        {
          text: 'Cancel',
          icon: 'close-outline',
          role: 'cancel'
        }
      ]
    });

    // Present the action sheet to trigger component initialization
    await actionSheet.present();
    
    // Dismiss after minimal delay to ensure component is fully loaded and cached
    setTimeout(async () => {
      await actionSheet.dismiss();
    }, 10);
  }

  /**
   * Modal cancel handler for testing purposes
   * Dismisses the modal with cancel role
   */
  cancel() {
    this.modal.dismiss(null, 'cancel');
  }

  /**
   * Modal confirm handler for testing purposes
   * Dismisses the modal with confirm role and passes the name data
   */
  confirm() {
    this.modal.dismiss(this.name, 'confirm');
  }

  /**
   * Modal dismiss event handler for testing purposes
   * Updates the message when modal is confirmed
   * 
   * @param event - The dismiss event containing role and data information
   */
  onWillDismiss(event: Event) {
    const ev = event as CustomEvent<OverlayEventDetail<string>>;
    if (ev.detail.role === 'confirm') {
      this.message = `Hello, ${ev.detail.data}!`;
    }
  }
}